import createErrorType from 'app/utils/createErrorType';
import { createExternallyControlledPromise } from 'app/utils/async';

export interface ICanceller {
  cancel: () => void;
  cancelToken: CancelToken;
}

class CancelledError extends createErrorType('CancelledError') {}

export function isCancelledError(error: unknown) {
  return error instanceof CancelledError;
}

export class CancelToken {
  private reason: undefined | CancelledError = undefined;
  public readonly promise: Promise<CancelledError>;

  private constructor(promise: Promise<CancelledError>) {
    this.promise = promise;
  }

  /**
   * This is loosely based on Axios's CancelToken abstraction:
   * https://github.com/axios/axios#cancellation
   *
   * The main idea behind this approach to cancellation of asynchronous tasks is
   * that cancellation is "don't care". When we request that a promise be
   * cancelled, we cannot actually force it to stop what it's doing. We are simply
   * indicating to the promise that we don't care about its result anymore, so it
   * can stop what it's doing. Beyond that, it's completely up to the promise to
   * decide if and how to honor that request. The proposal for promise
   * cancellation is also an inspiration for this, and it has more details about
   * the motivations: https://github.com/tc39/proposal-cancellation#architecture
   *
   * `CancelToken.create()` returns a `cancel` function and a `cancelToken` object.
   * The `cancelToken` object is basically just a way to detect if and when the
   * `cancel` method has been called. `cancelToken.isCancelled()` allows us to
   * check if `cancel()` has been called, and `cancelToken.promise` is a promise
   * that resolves with a `CancelledError` when `cancel()` has been called.
   */
  public static create(): ICanceller {
    const { resolve, promise } = createExternallyControlledPromise<CancelledError>();

    const cancelToken = new CancelToken(promise);

    const cancel = () => {
      if (!cancelToken.isCancelled()) {
        cancelToken.reason = new CancelledError();
        resolve(cancelToken.reason);
      }
    };

    return {
      cancel,
      cancelToken,
    };
  }

  public isCancelled() {
    return this.getReason() !== undefined;
  }

  public getReason() {
    return this.reason;
  }
}

/**
 * Takes a cancel token and returns a promise that will reject if the cancel
 * token is cancelled.
 *
 * This is mainly just to help with handling the case where a cancel token has
 * not been provided, in which case this returns a promise that never resolves.
 */
export function rejectOnCancel(
  cancelToken: CancelToken | undefined,
): Promise<unknown> {
  if (!cancelToken) {
    // Return a promise that never resolves/rejects if there is no cancel token
    return new Promise(() => {});
  }

  return cancelToken.promise.then(reason => {
    throw reason;
  });
}

/**
 * Takes a cancel token and throws a CancelledError if the cancel token is
 * already cancelled.
 */
export function throwIfCancelled(cancelToken: CancelToken | undefined) {
  if (cancelToken && cancelToken.isCancelled()) {
    throw cancelToken.getReason();
  }
}
